import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ActiveOrganizationStore } from './active-organization.store';
import { EntitlementOrganization, Entitlements } from './entitlements.model';
import { EntitlementsRepository } from './entitlements.repository';

export const ACTIVE_ORGANIZATION_STORAGE_KEY = 'activeOrganizationId';

@Injectable({ providedIn: 'root' })
export class EntitlementService {
  private readonly repository = inject(EntitlementsRepository);
  private readonly activeOrganization = inject(ActiveOrganizationStore);

  private readonly entitlementsState = signal<Entitlements | null>(null);
  private request$: Observable<Entitlements> | null = null;

  readonly entitlements = this.entitlementsState.asReadonly();
  readonly effectivePermissions = computed(
    () => this.entitlementsState()?.activeContext?.permissions ?? [],
  );
  readonly lacksPermissions = computed(
    () => this.entitlementsState() !== null && this.effectivePermissions().length === 0,
  );
  readonly holding = computed(() => this.entitlementsState()?.holding ?? null);
  readonly organizations = computed(() => this.entitlementsState()?.organizations ?? []);
  readonly activeOrganizationId = computed(
    () => this.entitlementsState()?.activeOrganization ?? null,
  );

  load(): Observable<Entitlements> {
    if (!this.request$) {
      this.request$ = this.track(this.fetchStoredOrDefault());
    }

    return this.request$;
  }

  switchOrganization(organizationId: string): Observable<Entitlements> {
    const fetch$ = this.repository.fetch(organizationId).pipe(
      map((entitlements) => {
        if (entitlements.activeOrganization !== organizationId) {
          throw new Error(`Organization ${organizationId} was not activated`);
        }
        return entitlements;
      }),
    );
    this.request$ = this.track(fetch$, this.request$);

    return this.request$;
  }

  hasPermission(permission: string): boolean {
    return this.effectivePermissions().includes(permission);
  }

  private fetchStoredOrDefault(): Observable<Entitlements> {
    const storedOrganizationId = sessionStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY);

    return this.repository.fetch().pipe(
      switchMap((entitlements) => {
        if (!storedOrganizationId || storedOrganizationId === entitlements.activeOrganization) {
          return of(entitlements);
        }
        const isKnownOrganization = (entitlements.organizations ?? []).some(
          (org) => org.id === storedOrganizationId,
        );
        if (!isKnownOrganization) return of(entitlements);

        return this.repository.fetch(storedOrganizationId).pipe(
          map((stored) => ({
            ...stored,
            organizations: mergeOrganizations(
              entitlements.organizations ?? [],
              stored.organizations ?? [],
            ),
          })),
          catchError(() => of(entitlements)),
        );
      }),
    );
  }

  private track(
    request$: Observable<Entitlements>,
    fallback$: Observable<Entitlements> | null = null,
  ): Observable<Entitlements> {
    return request$.pipe(
      map((entitlements) => this.withKnownOrganizations(entitlements)),
      tap((entitlements) => {
        this.entitlementsState.set(entitlements);
        sessionStorage.setItem(ACTIVE_ORGANIZATION_STORAGE_KEY, entitlements.activeOrganization);
        this.activeOrganization.setActive(
          entitlements.activeOrganization,
          this.organizationName(entitlements.activeOrganization),
        );
      }),
      catchError((error: unknown) => {
        this.request$ = fallback$;
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  private withKnownOrganizations(entitlements: Entitlements): Entitlements {
    return {
      ...entitlements,
      organizations: mergeOrganizations(this.organizations(), entitlements.organizations ?? []),
    };
  }

  private organizationName(organizationId: string): string {
    return this.organizations().find((org) => org.id === organizationId)?.name ?? '';
  }
}

function mergeOrganizations(
  known: readonly EntitlementOrganization[],
  incoming: readonly EntitlementOrganization[],
): EntitlementOrganization[] {
  const incomingById = new Map(incoming.map((org) => [org.id, org]));
  const merged = known.map((org) => incomingById.get(org.id) ?? org);
  const mergedIds = new Set(merged.map((org) => org.id));

  return [...merged, ...incoming.filter((org) => !mergedIds.has(org.id))];
}
